import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DepartmentTreeNode } from '../../model/department-tree-node.model';
import { DepartmentService } from '../../services/department.service';
import { DepartmentDto } from 'src/core/models/administration/department/department.dto';

@Component({
    selector: 'ca-department-tree-v2',
    templateUrl: './department-tree.component.html',
    styleUrls: ['./department-tree.component.scss'],
    standalone: false
})
export class DepartmentTreeComponent implements OnInit, OnChanges {
  @Input()
  quickSearchTerm: string | null = null;

  @Output()
  selectionChange: EventEmitter<DepartmentDto | null> = new EventEmitter();

  items: DepartmentTreeNode[] = [];
  loadingNodeId: number | null = null;
  selectedNodeId: number | null = null;
  loading: boolean = false;

  constructor(private service: DepartmentService) {
    this.loadRoot();
  }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quickSearchTerm && !changes.quickSearchTerm.isFirstChange()) {
      const fn = this.quickSearchTerm.trim().length > 0 ? this.filter : this.loadRoot;

      fn.apply(this);
    }
  }

  onClickToggleNode(node: DepartmentTreeNode, nodeIndex: number, eventArgs: MouseEvent): void {
    eventArgs.preventDefault();
    eventArgs.stopPropagation();

    if (this.loadingNodeId !== null) {
      return;
    } else if (!node.department.hasChildren) {
      return;
    }

    const fn = node.expanded ? this.collapseNode : this.expandNode;

    node.expanded = !node.expanded;

    fn.apply(this, [node, nodeIndex]);
  }

  onClickNode(node: DepartmentTreeNode, nodeIndex: number, eventArgs: MouseEvent): void {
    this.selectedNodeId = node.department.id;

    this.selectionChange.emit(node.department);
  }

  collapseAll(): void {
    this.items = this.items.filter(x => x.level === 1);

    this.items.forEach(x => {
      x.expanded = false;
    });
  }

  private expandNode(node: DepartmentTreeNode, nodeIndex: number): void {
    this.loadChildren(node, nodeIndex);
  }

  private collapseNode(node: DepartmentTreeNode, nodeIndex: number): void {
    let nextSiblingIndex = -1;
    let count = 0;

    for (let i = nodeIndex + 1; i < this.items.length; i++) {
      const item = this.items[i];

      if (item.level <= node.level) {
        nextSiblingIndex = i;

        break;
      }

      count++;
    }

    if (nextSiblingIndex > -1) {
      this.items.splice(nodeIndex + 1, count);
    }
  }

  private loadChildren(parent: DepartmentTreeNode, nodeIndex: number): void {
    this.loadingNodeId = parent.department.id;

    this.service.getChildDepartments(parent.department.id, 'active', false).subscribe(response => {
      const newItems = response.items.map(x => {
        const item: DepartmentTreeNode = {
          department: x,
          expanded: false,
          level: parent.level + 1,
          disabled: false,
        };

        return item;
      });

      this.items.splice(nodeIndex + 1, 0, ...newItems);

      this.loadingNodeId = null;
    });
  }

  private loadRoot(): void {
    this.loading = true;

    this.service.getRootDepartments('active', []).subscribe(response => {
      this.items = response.items.map(x => {
        const item: DepartmentTreeNode = {
          department: x,
          expanded: false,
          level: 1,
          disabled: false,
        };

        return item;
      });

      this.loading = false;
    });
  }

  private filter(): void {
    this.loading = true;

    this.service.getFilteredDepartments(this.quickSearchTerm, 'active').subscribe(response => {
      this.items = [];
      this.buildTree(response.items, null, 1);

      this.loading = false;
    });
  }

  private buildTree(departments: DepartmentDto[], parentId: number | null, level: number): void {
    departments
      .filter(x => x.parentId === parentId)
      .forEach(x => {
        const hasChildren = departments.filter(d => d.parentId === x.id).length > 0;

        const item: DepartmentTreeNode = {
          department: x,
          expanded: hasChildren,
          level: level,
          disabled: false,
        };

        this.items.push(item);

        this.buildTree(departments, x.id, level + 1);
      });
  }
}
